"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShoppingCart, Eye, FolderTree, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const focusPoints = [{
  id: 1,
  title: "Product visibility",
  description: "Rank individual SKUs and product variants for specific buyer queries.",
  icon: Eye
}, {
  id: 2,
  title: "Category visibility",
  description: "Capture high-volume non-branded searches across collection landing hubs.",
  icon: FolderTree
}, {
  id: 3,
  title: "Buyer-intent search",
  description: "Attract shoppers actively comparing features and ready to complete checkout.",
  icon: Target
}];
export default function Ecommerce() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="ecommerceseohero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 text-foreground">
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-secondary/30 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -left-24 h-80 w-80 rounded-full bg-muted/50 blur-3xl" />

      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Narrative and CTAs */}
          <div className="lg:col-span-7">
            <motion.div variants={itemVariants} className="mb-4 inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                <ShoppingCart className="mr-1.5 h-3.5 w-3.5 text-primary" />
                Independent Ecommerce SEO Consultant
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Ecommerce SEO Consultant
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-6 text-base text-muted-foreground sm:text-lg md:text-xl leading-relaxed max-w-2xl">
              I help UK online stores and international English-speaking retailers turn product searches into commercial performance. By improving organic visibility across product and category pages, I attract relevant traffic from shoppers searching with clear purchase intent. You speak directly with me, and I do the work.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="font-semibold text-base px-6">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium text-base">
                <Link to="/ContactPage?topic=free-seo-audit">
                  Get a Free SEO Audit
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-10 pt-8 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {focusPoints.map((point, i) => {
                const Icon = point.icon;
                return <div key={point.id} data-index={i} className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                          <Icon className="h-4 w-4" />
                        </div>
                        <p className="text-sm font-semibold text-foreground">
                          {point.title}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {point.description}
                      </p>
                    </div>;
              })}
              </div>
            </motion.div>
          </div>

          {/* Right Column: Consultant Photo Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5 relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md lg:max-w-none">
              <div className="relative bg-card rounded-2xl border border-border p-3 sm:p-4 shadow-xl overflow-hidden">
                <div className="relative rounded-xl overflow-hidden aspect-4/5 bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent Ecommerce SEO Consultant" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  <div className="absolute bottom-4 left-4 right-4 text-background">
                    <p className="text-xs font-medium tracking-wide uppercase text-background/80">
                      Independent Ecommerce SEO Consultant
                    </p>
                    <p className="text-lg font-bold font-default text-background">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-background/70">
                      Direct consultant access & bespoke strategy
                    </p>
                  </div>
                </div>

                {/* Caption Chip 1: Focus */}
                <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.6,
                duration: 0.5
              }} className="absolute -top-3 -left-3 sm:-left-4 bg-background border border-border rounded-xl p-3 shadow-lg flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <FolderTree className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground font-medium">Focus</p>
                    <p className="text-xs font-semibold text-foreground">
                      Product and category pages
                    </p>
                  </div>
                </motion.div>

                {/* Caption Chip 2: Search Intent */}
                <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.7,
                duration: 0.5
              }} className="absolute -bottom-3 -right-3 sm:-right-4 bg-background border border-border rounded-xl p-3 shadow-lg flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Target className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground font-medium">Search intent</p>
                    <p className="text-xs font-semibold text-foreground">
                      Shoppers ready to buy
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}